/**
 * <product-tabs>
 *
 * Renders as a tab bar on desktop and an accordion on mobile from a single set of markup —
 * the layout switch is pure CSS (see .product-tabs in custom.css). Both modes share one
 * state model: exactly one tab is active at a time. The only behavioural difference is that
 * on mobile, clicking the open tab collapses it; on desktop a tab bar always has a selection,
 * so clicking the active tab is a no-op.
 */
class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-product-tab]'));
    if (this.tabs.length === 0) return;

    this.desktop = window.matchMedia('(min-width: 750px)');

    this.tabs.forEach((tab, index) => {
      const label = tab.querySelector('[data-product-tab-label]');
      const panel = tab.querySelector('[data-product-tab-panel]');
      if (!label || !panel) return;

      // Ids are assigned here rather than in Liquid so the label/panel pairing can never
      // drift from the DOM order the CSS grid relies on.
      const uid = `${this.dataset.uid || 'product-tab'}-${index}`;
      label.id = `${uid}-label`;
      panel.id = `${uid}-panel`;
      label.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', label.id);

      label.addEventListener('click', () => this.onLabelClick(index));
      label.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.select(0);
  }

  onLabelClick(index) {
    const isOpen = this.tabs[index].dataset.active === 'true';
    if (isOpen && !this.desktop.matches) {
      this.select(-1); // Collapse on mobile only.
    } else {
      this.select(index);
    }
  }

  onKeydown(event, index) {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step || !this.desktop.matches) return;

    event.preventDefault();
    const next = (index + step + this.tabs.length) % this.tabs.length;
    this.select(next);
    this.tabs[next].querySelector('[data-product-tab-label]').focus();
  }

  select(activeIndex) {
    this.tabs.forEach((tab, index) => {
      const isActive = index === activeIndex;
      tab.dataset.active = String(isActive);
      tab.querySelector('[data-product-tab-label]').setAttribute('aria-expanded', String(isActive));
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
